import { useEffect } from 'react';

interface StructuredDataProps {
  type: 'SportsEvent' | 'Organization' | 'ItemList' | 'WebPage' | 'Product' | 'Article' | 'BreadcrumbList';
  data: any;
}

// Helper to format dates as ISO 8601
const toISO8601 = (date: string | Date | undefined): string => {
  if (!date) return new Date().toISOString();
  const d = new Date(date);
  return isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

export function StructuredData({ type, data }: StructuredDataProps) {
  useEffect(() => {
    let schema: any = {};

    switch (type) {
      case 'SportsEvent':
        schema = {
          "@context": "https://schema.org",
          "@type": "SportsEvent",
          "name": data.match || data.name,
          "sport": data.sport,
          "startDate": toISO8601(data.match_date || data.startDate),
          "location": {
            "@type": "Place",
            "name": data.league || "Online"
          },
          "competitor": [
            { "@type": "SportsTeam", "name": data.homeTeam || data.match?.split(' vs ')?.[0] || "Team A" },
            { "@type": "SportsTeam", "name": data.awayTeam || data.match?.split(' vs ')?.[1] || "Team B" }
          ],
          "offers": data.price ? {
            "@type": "Offer",
            "price": data.price,
            "priceCurrency": data.currency || "USD",
            "availability": "https://schema.org/InStock",
            "validFrom": toISO8601(new Date())
          } : undefined
        };
        break;

      case 'Organization':
        schema = {
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "BetVerse",
          "url": "https://betverse-global-picks.lovable.app",
          "logo": "https://betverse-global-picks.lovable.app/favicon.ico",
          "description": "AI-powered betting tips marketplace connecting verified tipsters worldwide",
          "sameAs": [
            "https://twitter.com/betverse",
            "https://t.me/betverse"
          ],
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": data.avgRating || 4.7,
            "reviewCount": data.totalReviews || 15000,
            "bestRating": 5,
            "worstRating": 1
          }
        };
        break;

      case 'ItemList':
        schema = {
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": data.title || "Betting Tips Leaderboard",
          "numberOfItems": data.items?.length || 0,
          "itemListElement": (data.items || []).map((item: any, index: number) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Person",
              "name": item.name,
              "description": `${item.winRate}% win rate, ${item.roi}% ROI`,
              "url": `https://betverse-global-picks.lovable.app/tipsters/${item.id || index + 1}`
            }
          }))
        };
        break;

      case 'WebPage':
        schema = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": data.title,
          "description": data.description,
          "url": data.url,
          "isPartOf": {
            "@type": "WebSite",
            "name": "BetVerse",
            "url": "https://betverse-global-picks.lovable.app"
          }
        };
        break;

      case 'Product':
        schema = {
          "@context": "https://schema.org",
          "@type": "Product",
          "name": data.name,
          "description": data.description,
          "brand": {
            "@type": "Organization",
            "name": data.brand || "BetVerse"
          },
          "offers": {
            "@type": "Offer",
            "price": data.price,
            "priceCurrency": data.priceCurrency || "USD",
            "availability": `https://schema.org/${data.availability || 'InStock'}`,
            "url": data.url,
            "priceValidUntil": toISO8601(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
          }
        };
        break;

      case 'Article':
        schema = {
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": data.headline,
          "description": data.description,
          "image": data.image || "https://betverse-global-picks.lovable.app/favicon.ico",
          "author": {
            "@type": "Person",
            "name": data.author || "BetVerse Team"
          },
          "datePublished": toISO8601(data.datePublished),
          "dateModified": toISO8601(data.dateModified || data.datePublished),
          "publisher": {
            "@type": "Organization",
            "name": "BetVerse",
            "logo": {
              "@type": "ImageObject",
              "url": "https://betverse-global-picks.lovable.app/favicon.ico"
            }
          },
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": data.url
          }
        };
        break;

      case 'BreadcrumbList':
        schema = {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": (data.items || []).map((item: any, index: number) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": item.name,
            "item": item.url
          }))
        };
        break;
    }

    // Remove undefined values
    const cleanSchema = JSON.parse(JSON.stringify(schema));

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(cleanSchema);
    script.id = `structured-data-${type.toLowerCase()}-${Date.now()}`;
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById(script.id);
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, [type, data]);

  return null;
}
